"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import {
  AlertCircleIcon,
  FileTextIcon,
  PaperclipIcon,
  XIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
} from "@/components/ai-elements/prompt-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { isHighTierModelId } from "@/lib/ai/model-tier";
import { cn } from "@/lib/utils";
import { AnalysisTargetInputHighlight } from "./analysis-target-input-highlight";
import { AnalysisTargetMentionMenu } from "./analysis-target-mention";
import { AnalysisTargetPicker } from "./analysis-target-picker";
import { AnalysisTargetSelectedContexts } from "./analysis-target-selected-contexts";
import { ChatPromptDisclaimer } from "./chat-prompt-disclaimer";
import { ChatPromptSpeechButton } from "./chat-prompt-speech-button";
import { ChatPromptSpeechInterimOverlay } from "./chat-prompt-speech-interim-overlay";
import { useChatPromptSurfaceContext } from "./chat-prompt-surface-context";
import {
  CHAT_SEND_ATTACHMENT_ACCEPT,
  type ChatSendFileValidationError,
} from "./chat-send-input";
import { ModelSelectMenu } from "./model-select-menu";

type ChatPromptSurfaceInputProps = {
  placeholder: string;
  showDisclaimer?: boolean;
  floatingDisclaimer?: boolean;
  disclaimerPlacement?: "before-input" | "after-input";
  hideControlsWhenCompact?: boolean;
  inputHeader?: ReactNode;
  bodyClassName?: string;
  textareaClassName?: string;
  expandedTextareaClassName?: string;
  compactTextareaClassName?: string;
  toolbarClassName?: string;
  expandedToolbarClassName?: string;
  compactToolbarClassName?: string;
  toolbarEnd?: ReactNode;
};

function getAttachmentErrorKey(error: ChatSendFileValidationError): string {
  switch (error.code) {
    case "max-count":
      return `${error.code}-${error.maxCount}`;
    case "unsupported-type":
    case "max-size":
    case "invalid-text-encoding":
    case "max-text-size":
      return `${error.code}-${error.filename}`;
  }
}

/**
 * `ChatPromptSurface` の共有入力本体です。
 *
 * analysis target、mention highlight、model picker、submit button、disclaimer を
 * 一体で描画します。この component を使うことで、starter と composer のどちらでも
 * 同じ送信契約と quota 表示を維持できます。
 */
export function ChatPromptSurfaceInput({
  placeholder,
  showDisclaimer,
  floatingDisclaimer = false,
  disclaimerPlacement = "after-input",
  hideControlsWhenCompact = false,
  inputHeader,
  bodyClassName,
  textareaClassName,
  expandedTextareaClassName,
  compactTextareaClassName,
  toolbarClassName,
  expandedToolbarClassName,
  compactToolbarClassName,
  toolbarEnd,
}: ChatPromptSurfaceInputProps) {
  const { t } = useLingui();
  const {
    isExpanded,
    canSubmit,
    isInteractive,
    isAttachmentDropActive,
    optionsState,
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    addAnalysisTarget,
    mentionedTargets,
    mentionInstances,
    mentionMatch,
    mentionAnchor,
    mentionMenuOpen,
    mentionActiveOptionKey,
    setMentionActiveOptionKey,
    handleMentionTargetSelect,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleTextareaKeyUp,
    handleTextareaClick,
    textareaElement,
    bindTextarea,
    optionMetadataByKey,
    showInputOverlay,
    handleRemoveTarget,
    handleSubmit,
    attachments,
    attachmentErrors,
    fileInputId,
    attachmentInputRef,
    handleAttachmentButtonClick,
    handleAttachmentChange,
    handleAttachmentDragEnter,
    handleAttachmentDragLeave,
    handleAttachmentDragOver,
    handleAttachmentDrop,
    handleAttachmentRemove,
    handleAttachmentErrorDismiss,
    handleAttachmentPaste,
    textareaRows,
    model,
    highQuotaIndicator,
    lowQuotaIndicator,
    setContextPickerOpen,
    setModelSelectOpen,
    handleModelValueChange,
    input,
    speechInterimTranscript,
  } = useChatPromptSurfaceContext("ChatPromptSurfaceInput");
  const disclaimerVisible = showDisclaimer ?? isExpanded;

  const disclaimer =
    showDisclaimer === undefined && !isExpanded ? null : (
      <ChatPromptDisclaimer
        floating={floatingDisclaimer}
        placement={disclaimerPlacement}
        show={disclaimerVisible}
      />
    );
  const isSpeechInterimActive = speechInterimTranscript.length > 0;
  // 仮入力を灰色イタリックのオーバーレイで表示している間は、placeholder と重なって
  // 読みづらくなるため placeholder を隠す。
  const effectivePlaceholder = isSpeechInterimActive ? "" : placeholder;

  return (
    <div className="relative space-y-1.5">
      {disclaimerPlacement === "before-input" ? disclaimer : null}
      {inputHeader}
      <PromptInput
        className="relative"
        inputGroupClassName={cn(
          "relative rounded-2xl border-border/70 bg-background shadow-sm transition-[background-color,box-shadow] duration-150",
          isAttachmentDropActive && "bg-primary/5 ring-2 ring-primary/40",
        )}
        onDragEnter={handleAttachmentDragEnter}
        onDragLeave={handleAttachmentDragLeave}
        onDragOver={handleAttachmentDragOver}
        onDrop={handleAttachmentDrop}
        onSubmit={handleSubmit}
      >
        <PromptInputBody className={bodyClassName}>
          <AnalysisTargetSelectedContexts
            pageTarget={pageAnalysisTarget}
            manualTargets={manualAnalysisTargets}
            mentionedTargets={mentionedTargets}
            onRemoveTarget={handleRemoveTarget}
            displayMode={isExpanded ? "full" : "summary"}
          />
          {/* 仮入力表示中は interim オーバーレイが確定テキストも描画するため、
              二重描画を避けて mention 装飾オーバーレイを抑止する。 */}
          {isSpeechInterimActive ? null : (
            <AnalysisTargetInputHighlight
              textarea={textareaElement}
              value={input}
              mentions={mentionInstances}
              optionMetadataByKey={optionMetadataByKey}
            />
          )}
          {isSpeechInterimActive ? (
            <ChatPromptSpeechInterimOverlay
              textClassName={cn(
                textareaClassName,
                isExpanded
                  ? expandedTextareaClassName
                  : compactTextareaClassName,
              )}
            />
          ) : null}
          <input
            ref={attachmentInputRef}
            id={fileInputId}
            type="file"
            className="sr-only"
            accept={CHAT_SEND_ATTACHMENT_ACCEPT.join(",")}
            multiple
            disabled={!isInteractive}
            onChange={handleAttachmentChange}
          />
          <PromptInputTextarea
            ref={bindTextarea}
            compact={!isExpanded}
            className={cn(
              "relative z-20 transition-[min-height,padding,font-size,line-height] duration-200 ease-out",
              textareaClassName,
              isExpanded ? expandedTextareaClassName : compactTextareaClassName,
              (showInputOverlay || isSpeechInterimActive) &&
                "text-transparent caret-foreground",
            )}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            onKeyUp={handleTextareaKeyUp}
            onClick={handleTextareaClick}
            onPaste={handleAttachmentPaste}
            placeholder={effectivePlaceholder}
            rows={textareaRows}
            aria-describedby={
              disclaimerVisible ? "prompt-input-disclaimer" : undefined
            }
            disabled={!isInteractive}
          />
          {attachments.length > 0 ? (
            <div
              data-align="block-start"
              className="relative z-20 flex w-full flex-wrap items-start justify-start gap-2 px-3 pb-2"
            >
              {attachments.map((attachment) => {
                const filename = attachment.part.filename ?? "Attachment";
                const isImage =
                  attachment.part.mediaType?.startsWith("image/") &&
                  attachment.part.url;

                return (
                  <div
                    key={attachment.id}
                    className={cn(
                      "group relative overflow-hidden rounded-md border border-border bg-muted/40 text-xs text-foreground",
                      isImage
                        ? "h-20 w-20"
                        : "flex h-8 max-w-full items-center gap-2 px-2",
                    )}
                  >
                    {isImage ? (
                      <img
                        src={attachment.part.url}
                        alt={filename}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <>
                        <FileTextIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{filename}</span>
                      </>
                    )}
                    <button
                      type="button"
                      className={cn(
                        "rounded-sm text-muted-foreground hover:text-foreground",
                        isImage &&
                          "absolute right-1 top-1 bg-background/90 p-0.5 shadow-sm opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
                      )}
                      onClick={() => handleAttachmentRemove(attachment.id)}
                      aria-label={t`Remove attachment`}
                      title={t`Remove attachment`}
                      disabled={!isInteractive}
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
          {attachmentErrors.length > 0 ? (
            <div className="relative z-20 px-3 pb-2">
              {attachmentErrors.map((error) => (
                <Alert
                  key={getAttachmentErrorKey(error)}
                  variant="destructive"
                  className="pr-8 text-left text-xs"
                >
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription className="text-left text-xs">
                    {error.code === "max-count"
                      ? t`You can attach up to ${error.maxCount} files.`
                      : error.code === "unsupported-type"
                        ? t`${error.filename} is not a supported file type.`
                        : error.code === "invalid-text-encoding"
                          ? t`${error.filename} must be encoded in UTF-8.`
                          : error.code === "max-text-size"
                            ? t`${error.filename} exceeds the ${error.maxKilobytes} KB text file size limit.`
                            : t`${error.filename} exceeds the ${error.maxMegabytes} MB file size limit.`}
                  </AlertDescription>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1.5 top-1.5 size-6 text-destructive hover:text-destructive"
                    onClick={() => handleAttachmentErrorDismiss(error)}
                    aria-label={t`Dismiss`}
                    title={t`Dismiss`}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                </Alert>
              ))}
            </div>
          ) : null}
          <AnalysisTargetMentionMenu
            open={mentionMenuOpen}
            query={mentionMatch?.query ?? ""}
            optionsState={optionsState}
            onSelectTarget={handleMentionTargetSelect}
            anchor={mentionAnchor}
            activeOptionKey={mentionActiveOptionKey}
            onActiveOptionKeyChange={setMentionActiveOptionKey}
          />
        </PromptInputBody>
        {isAttachmentDropActive ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/70 bg-background/80 text-sm font-medium text-foreground shadow-inner backdrop-blur-[1px]"
          >
            <Trans>Drop files to attach</Trans>
          </div>
        ) : null}
        <PromptInputToolbar
          compact={!isExpanded}
          className={cn(
            "min-w-0",
            toolbarClassName,
            isExpanded ? expandedToolbarClassName : compactToolbarClassName,
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div
              aria-hidden={hideControlsWhenCompact && !isExpanded}
              className="contents"
              hidden={hideControlsWhenCompact && !isExpanded}
            >
              <AnalysisTargetPicker
                optionsState={optionsState}
                selectedTargets={analysisTargets}
                onSelectTarget={addAnalysisTarget}
                disabled={!isInteractive || mentionMenuOpen}
                onOpenChange={setContextPickerOpen}
              />
            </div>
            <PromptInputButton
              type="button"
              onClick={handleAttachmentButtonClick}
              variant="ghost"
              className="rounded-full text-muted-foreground hover:text-foreground"
              title={t`Attach file`}
              aria-label={t`Attach file`}
              disabled={!isInteractive}
            >
              <PaperclipIcon className="h-4 w-4" />
            </PromptInputButton>
            <ChatPromptSpeechButton />
            <div
              aria-hidden={hideControlsWhenCompact && !isExpanded}
              className="contents"
              hidden={hideControlsWhenCompact && !isExpanded}
            >
              <ModelSelectMenu
                model={model}
                quotaState={
                  isHighTierModelId(model)
                    ? highQuotaIndicator
                    : lowQuotaIndicator
                }
                disabled={!isInteractive}
                onOpenChange={setModelSelectOpen}
                onValueChange={handleModelValueChange}
              />
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {toolbarEnd}
            <PromptInputSubmit
              size="sm"
              aria-label={t`Submit`}
              disabled={!canSubmit}
            >
              <Trans>Submit</Trans>
            </PromptInputSubmit>
          </div>
        </PromptInputToolbar>
      </PromptInput>
      {disclaimerPlacement === "after-input" ? disclaimer : null}
    </div>
  );
}
